import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/lib/toast';
import { Collaborator, CollaboratorInvite, CollaboratorPermissions } from '@/types/collaborator';
import { Json } from '@/integrations/supabase/types';

// Helper function to convert Json to CollaboratorPermissions
const jsonToPermissions = (json: Json | null): CollaboratorPermissions => {
  const defaultPermissions: CollaboratorPermissions = {
    posts: true,
    clients: true,
    reports: true,
    calendar: true,
    finances: false,
    workflow: true,
    approvals: true,
    automation: true
  };

  if (!json || typeof json !== 'object') return defaultPermissions;
  
  const jsonObj = json as Record<string, any>;
  return {
    posts: Boolean(jsonObj.posts ?? defaultPermissions.posts),
    clients: Boolean(jsonObj.clients ?? defaultPermissions.clients),
    reports: Boolean(jsonObj.reports ?? defaultPermissions.reports),
    calendar: Boolean(jsonObj.calendar ?? defaultPermissions.calendar),
    finances: Boolean(jsonObj.finances ?? defaultPermissions.finances),
    workflow: Boolean(jsonObj.workflow ?? defaultPermissions.workflow),
    approvals: Boolean(jsonObj.approvals ?? defaultPermissions.approvals),
    automation: Boolean(jsonObj.automation ?? defaultPermissions.automation)
  };
};

// Helper function to convert CollaboratorPermissions to Json
const permissionsToJson = (permissions: CollaboratorPermissions): Json => {
  return permissions as unknown as Json;
};

// Helper function to add collaborator to all company records visibility
const addCollaboratorToAllRecords = async (companyId: string, userId: string) => {
  try {
    console.log('Adding collaborator to all company records:', { companyId, userId });

    // Get all company owners and collaborators
    const { data: companyData } = await supabase
      .from('companies')
      .select('owner_ids')
      .eq('id', companyId)
      .single();

    const { data: collaborators } = await supabase
      .from('collaborators')
      .select('collaborator_id')
      .eq('company_id', companyId)
      .eq('status', 'active');

    // Create complete visibility list
    let allVisibleUsers: string[] = [];
    
    if (companyData?.owner_ids && Array.isArray(companyData.owner_ids)) {
      allVisibleUsers = [...companyData.owner_ids.map(id => String(id))];
    }
    
    if (collaborators) {
      collaborators.forEach(c => {
        const collaboratorId = String(c.collaborator_id);
        if (!allVisibleUsers.includes(collaboratorId)) {
          allVisibleUsers.push(collaboratorId);
        }
      });
    }

    console.log('All visible users:', allVisibleUsers);

    // Update clients
    const { data: clients } = await supabase
      .from('clients')
      .select('id')
      .eq('company_id', companyId);

    if (clients) {
      for (const client of clients) {
        await supabase
          .from('clients')
          .update({ visible_to: allVisibleUsers })
          .eq('id', client.id);
      }
      console.log(`Updated ${clients.length} clients`);
    }

    // Update approval posts
    const { data: approvals } = await supabase
      .from('approval_posts')
      .select('id')
      .eq('company_id', companyId);

    if (approvals) {
      for (const approval of approvals) {
        await supabase
          .from('approval_posts')
          .update({ visible_to: allVisibleUsers })
          .eq('id', approval.id);
      }
      console.log(`Updated ${approvals.length} approval posts`);
    }

    // Update goals
    const { data: goals } = await supabase
      .from('goals')
      .select('id')
      .eq('company_id', companyId);

    if (goals) {
      for (const goal of goals) {
        await supabase
          .from('goals')
          .update({ visible_to: allVisibleUsers })
          .eq('id', goal.id);
      }
      console.log(`Updated ${goals.length} goals`);
    }

    // Update kanban boards
    const { data: boards } = await supabase
      .from('kanban_boards')
      .select('id')
      .eq('company_id', companyId);

    if (boards) {
      for (const board of boards) {
        await supabase
          .from('kanban_boards')
          .update({ visible_to: allVisibleUsers })
          .eq('id', board.id);
      }
      console.log(`Updated ${boards.length} kanban boards`);
    }

    console.log('Successfully updated all company records visibility');
  } catch (error) {
    console.error('Error updating company records visibility:', error);
    throw error;
  }
};

export function useCollaborators() {
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [invites, setInvites] = useState<CollaboratorInvite[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadCollaborators = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // Get current user
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      // Get user's company_id
      const { data: userData } = await supabase
        .from('users')
        .select('company_id')
        .eq('id', session.user.id)
        .single();

      if (!userData?.company_id) return;
      
      // Load collaborators with user info
      const { data: collaboratorData, error: collaboratorError } = await supabase
        .from('collaborators')
        .select(`
          id,
          company_id,
          collaborator_id,
          permissions,
          status,
          created_at,
          updated_at
        `)
        .eq('company_id', userData.company_id);

      if (collaboratorError) {
        console.error("Error loading collaborators:", collaboratorError);
        setCollaborators([]);
      } else if (collaboratorData) {
        // Get user info for each collaborator
        const collaboratorsWithUserInfo: Collaborator[] = [];
        
        for (const collab of collaboratorData) {
          const { data: userInfo } = await supabase
            .from('users')
            .select('name, email')
            .eq('id', collab.collaborator_id)
            .single();
            
          collaboratorsWithUserInfo.push({
            ...collab,
            collaborator_name: userInfo?.name,
            collaborator_email: userInfo?.email,
            permissions: jsonToPermissions(collab.permissions),
            status: collab.status as 'active' | 'inactive'
          });
        }
        
        setCollaborators(collaboratorsWithUserInfo);
      }

      // Load pending invites
      const { data: inviteData, error: inviteError } = await supabase
        .from('collaborator_invites')
        .select('*')
        .eq('company_id', userData.company_id)
        .eq('status', 'pending');

      if (inviteError) {
        console.error("Error loading invites:", inviteError);
        setInvites([]);
      } else if (inviteData) {
        setInvites(inviteData.map(invite => ({
          ...invite,
          permissions: jsonToPermissions(invite.permissions),
          status: invite.status as 'pending' | 'accepted' | 'declined' | 'expired'
        })));
      }
      
    } catch (error) {
      console.error("Error loading collaborator data:", error);
      toast.error("Erro ao carregar dados dos colaboradores");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCollaborators();
  }, [loadCollaborators]);

  const inviteCollaborator = async (email: string, permissions: CollaboratorPermissions) => {
    try {
      console.log('Starting invite process for:', email);
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error("Usuário não autenticado");
        return false;
      }

      // Get user's company_id
      const { data: userData } = await supabase
        .from('users')
        .select('company_id')
        .eq('id', session.user.id)
        .single();

      if (!userData?.company_id) {
        toast.error("Empresa não encontrada");
        return false;
      }

      const trimmedEmail = email.toLowerCase().trim();
      
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(trimmedEmail)) {
        toast.error("Email inválido");
        return false;
      }

      // Cannot invite yourself
      if (trimmedEmail === session.user.email) {
        toast.error("Você não pode convidar a si mesmo");
        return false;
      }

      console.log('Checking for existing collaborator relationship');
      
      // Check if there's already a collaboration by checking if user exists in our users table
      const { data: existingUser } = await supabase
        .from('users')
        .select('id')
        .eq('email', trimmedEmail)
        .maybeSingle();
      
      if (existingUser) {
        console.log('User exists, checking collaboration');
        const { data: existingCollab } = await supabase
          .from('collaborators')
          .select('id')
          .eq('company_id', userData.company_id)
          .eq('collaborator_id', existingUser.id)
          .maybeSingle();

        if (existingCollab) {
          toast.error("Este usuário já é um colaborador");
          return false;
        }
      }

      console.log('Checking for existing pending invite');
      
      // Check if there's already a pending invite
      const { data: existingInvite } = await supabase
        .from('collaborator_invites')
        .select('id, status')
        .eq('company_id', userData.company_id)
        .eq('email', trimmedEmail)
        .in('status', ['pending'])
        .maybeSingle();

      if (existingInvite) {
        toast.error("Já existe um convite pendente para este email");
        return false;
      }

      console.log('Creating invitation');

      // First, clean up any old expired invites for this email/company combination
      await supabase
        .from('collaborator_invites')
        .delete()
        .eq('company_id', userData.company_id)
        .eq('email', trimmedEmail)
        .lt('expires_at', new Date().toISOString());

      // Create invitation with explicit error handling
      const { data: inviteData, error: inviteError } = await supabase
        .from('collaborator_invites')
        .insert({
          company_id: userData.company_id,
          email: trimmedEmail,
          permissions: permissionsToJson(permissions),
          status: 'pending'
        })
        .select()
        .single();

      if (inviteError) {
        console.error('Error creating invite:', inviteError);
        
        // Handle specific duplicate key error
        if (inviteError.code === '23505') {
          toast.error("Já existe um convite para este email. Tente novamente em alguns segundos.");
        } else {
          toast.error("Erro ao criar convite: " + inviteError.message);
        }
        return false;
      }

      console.log('Invite created successfully:', inviteData);

      // If user already exists, automatically create the collaboration and add to all records
      if (existingUser) {
        console.log('User already exists, creating collaboration automatically');
        
        // Create collaboration
        const { error: collabError } = await supabase
          .from('collaborators')
          .insert({
            company_id: userData.company_id,
            collaborator_id: existingUser.id,
            permissions: permissionsToJson(permissions),
            status: 'active'
          });

        if (collabError) {
          console.error('Error creating collaboration:', collabError);
        } else {
          console.log('Collaboration created, now adding to all company records');
          
          // Add collaborator to all existing company records
          try {
            await addCollaboratorToAllRecords(userData.company_id, existingUser.id);
            console.log('Successfully added collaborator to all company records');
          } catch (error) {
            console.error('Error adding collaborator to company records:', error);
            // Don't fail the invite for this, just log the error
          }
        }

        // Mark invite as accepted
        await supabase
          .from('collaborator_invites')
          .update({ status: 'accepted', accepted_at: new Date().toISOString() })
          .eq('id', inviteData.id);
      }

      // Try to send email notification (optional)
      try {
        const { data: companyData } = await supabase
          .from('users')
          .select('name, agency_name')
          .eq('id', session.user.id)
          .single();

        console.log('Attempting to send email notification');

        const { error: emailError } = await supabase.functions.invoke('send-collaborator-invite', {
          body: {
            email: trimmedEmail,
            inviterName: companyData?.name || 'Alguém',
            companyName: companyData?.agency_name || '',
            token: inviteData.token,
            permissions
          }
        });

        if (emailError) {
          console.warn('Email notification failed (non-critical):', emailError);
        }
      } catch (emailError) {
        console.warn('Email function error (non-critical):', emailError);
      }

      await loadCollaborators();
      toast.success(`Convite enviado para ${trimmedEmail}`);
      return true;
      
    } catch (error: any) {
      console.error("Error inviting collaborator:", error);
      
      // Handle specific database constraint errors
      if (error.code === '23505') {
        toast.error("Já existe um convite para este email. Aguarde alguns segundos e tente novamente.");
      } else {
        toast.error("Erro ao enviar convite. Tente novamente.");
      }
      return false;
    }
  };

  const removeCollaborator = async (collaboratorId: string) => {
    try {
      const { error } = await supabase
        .from('collaborators')
        .delete()
        .eq('id', collaboratorId);

      if (error) throw error;

      setCollaborators(collaborators.filter(c => c.id !== collaboratorId));
      toast.success("Colaborador removido com sucesso");
      return true;
    } catch (error) {
      console.error("Error removing collaborator:", error);
      toast.error("Erro ao remover colaborador");
      return false;
    }
  };

  const cancelInvite = async (inviteId: string) => {
    try {
      const { error } = await supabase
        .from('collaborator_invites')
        .delete()
        .eq('id', inviteId);

      if (error) throw error;

      setInvites(invites.filter(i => i.id !== inviteId));
      toast.success("Convite cancelado com sucesso");
      return true;
    } catch (error) {
      console.error("Error cancelling invite:", error);
      toast.error("Erro ao cancelar convite");
      return false;
    }
  };

  const updateCollaboratorPermissions = async (collaboratorId: string, permissions: CollaboratorPermissions) => {
    try {
      const { error } = await supabase
        .from('collaborators')
        .update({ permissions: permissionsToJson(permissions) })
        .eq('id', collaboratorId);

      if (error) throw error;

      setCollaborators(collaborators.map(c => 
        c.id === collaboratorId ? { ...c, permissions } : c
      ));
      
      toast.success("Permissões atualizadas com sucesso");
      return true;
    } catch (error) {
      console.error("Error updating permissions:", error);
      toast.error("Erro ao atualizar permissões");
      return false;
    }
  };

  return {
    collaborators,
    invites,
    isLoading,
    inviteCollaborator,
    removeCollaborator,
    cancelInvite,
    updateCollaboratorPermissions,
    refreshCollaborators: loadCollaborators
  };
}
